import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import type { WireSourceInstance, WireSourceKind } from '@logistics/contracts';
import { api } from '../client';
import { SOURCE_HEALTH_KEY } from '../health/use-source-health';
import { OVERVIEW_KEY } from '../overview/use-overview';
import { expectStatus } from '@/lib/result';

export const KINDS_KEY = ['sources', 'kinds'] as const;
export const INSTANCES_KEY = ['sources', 'instances'] as const;

/** What can be configured here: one descriptor per plugin the API was built with. */
export function useKinds(): UseQueryResult<WireSourceKind[]> {
  return useQuery({
    queryKey: KINDS_KEY,
    queryFn: async () => expectStatus(await api.instances.kinds(), 200).kinds,
    staleTime: 5 * 60_000,
  });
}

export function useInstances(): UseQueryResult<WireSourceInstance[]> {
  return useQuery({
    queryKey: INSTANCES_KEY,
    queryFn: async () => expectStatus(await api.instances.list({ query: {} }), 200).instances,
  });
}

/** After any change to a source: the list, its health, and the overview's counts. */
export function useRefreshSources(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: INSTANCES_KEY }),
      client.invalidateQueries({ queryKey: SOURCE_HEALTH_KEY }),
      client.invalidateQueries({ queryKey: OVERVIEW_KEY }),
    ]);
  };
}
